'use client'

import {
  FullBleedImage,
  OfferFrame,
  SafeText,
  clampIndex,
  getDesign,
  getOfferCopy,
  getOfferVisual,
  type OfferPackProps,
} from '../shared/OfferPackCore'

const bg = [
  'https://images.unsplash.com/photo-1497366754035-f200968a6e72?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1556761175-b413da4baf72?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1556761175-4b46a572b786?q=80&w=1400&auto=format&fit=crop',
]

const total = 5
const sans = '"Inter", "Helvetica Neue", Arial, sans-serif'
const mono = 'ui-monospace, SFMono-Regular, Menlo, monospace'

export default function CorporateOfferExecutivePack({
  draft,
  activeIndex = 0,
  onActiveIndexChange,
}: OfferPackProps) {
  const index = clampIndex(activeIndex, total)
  const design = getDesign(draft, '#2457D6')
  const accent = design.accent
  const navy = '#0F172A'
  const ink = '#111827'
  const muted = 'rgba(17,24,39,0.58)'
  const surface = '#F3F5F7'

  const copy = getOfferCopy(draft, index, {
    eyebrow: 'Executive offer',
    headline: 'Una solución más clara para crecer con control',
    subheadline: 'Diseñado para equipos que necesitan estructura, visibilidad y ejecución sin fricción.',
    offer: 'Paquete estratégico de implementación',
    proof: 'Menos fricción operativa. Más velocidad comercial.',
    urgency: 'Cierre de implementación mensual',
    cta: 'Solicitar propuesta',
    bullets: ['Diagnóstico ejecutivo', 'Plan de implementación', 'Seguimiento operativo'],
  })

  return (
    <OfferFrame
      activeIndex={index}
      total={total}
      onActiveIndexChange={onActiveIndexChange}
      text={index === 0 ? '#ffffff' : ink}
      className="bg-[#F3F5F7]"
    >
      {index === 0 ? (
        <>
          <FullBleedImage {...getOfferVisual(draft, index, bg[0], { overlay: 'black', opacity: 0.38 })} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/90 via-[#0F172A]/54 to-[#0F172A]/8" />
          <div className="absolute left-8 top-8 text-[11px] font-black uppercase tracking-[0.24em] text-white/70" style={{ fontFamily: mono }}>
            OFFER / EXECUTIVE
          </div>
          <div className="absolute bottom-16 left-8 max-w-[70%] text-white">
            <SafeText className="text-[clamp(40px,9vw,64px)] font-extrabold leading-[0.92] tracking-[-0.055em]" style={{ fontFamily: sans }}>
              {copy.headline}
            </SafeText>
            <SafeText className="mt-5 max-w-[74%] text-[15px] leading-[1.45] text-white/74" style={{ fontFamily: sans }}>
              {copy.subheadline}
            </SafeText>
          </div>
          <div className="absolute bottom-8 right-8 rounded-full px-5 py-3 text-[11px] font-black uppercase tracking-[0.16em] text-white" style={{ background: accent, fontFamily: sans }}>
            {copy.cta}
          </div>
        </>
      ) : null}

      {index === 1 ? (
        <>
          <div className="absolute inset-0" style={{ background: surface }} />
          <div className="absolute left-8 top-8 text-[11px] font-black uppercase tracking-[0.22em]" style={{ color: accent, fontFamily: mono }}>
            SCOPE
          </div>
          <div className="absolute left-8 top-20 max-w-[58%]" style={{ color: ink }}>
            <SafeText className="text-[42px] font-extrabold leading-[0.96] tracking-[-0.045em]" style={{ fontFamily: sans }}>
              {copy.offer || 'Implementación enfocada en resultados medibles.'}
            </SafeText>
          </div>
          <div className="absolute bottom-8 left-8 right-8 grid grid-cols-3 gap-3">
            {(copy.bullets.length ? copy.bullets : ['Diagnóstico', 'Ruta', 'Ejecución']).slice(0, 3).map((item, idx) => (
              <div key={idx} className="min-h-[148px] rounded-[26px] bg-white p-5 shadow-sm">
                <div className="text-[11px] font-black" style={{ color: accent, fontFamily: mono }}>0{idx + 1}</div>
                <SafeText className="mt-6 text-[18px] font-bold leading-[1.08] tracking-[-0.02em]" style={{ color: ink, fontFamily: sans }}>
                  {item}
                </SafeText>
              </div>
            ))}
          </div>
          <div className="absolute right-8 top-8 h-[210px] w-[210px] overflow-hidden rounded-full opacity-90">
            <FullBleedImage {...getOfferVisual(draft, index, bg[1], { overlay: 'white', opacity: 0.05 })} />
          </div>
        </>
      ) : null}

      {index === 2 ? (
        <>
          <div className="absolute inset-0" style={{ background: '#FFFFFF' }} />
          <div className="absolute left-0 top-0 h-full w-[12px]" style={{ background: accent }} />
          <div className="absolute left-10 top-10 right-10 flex items-start justify-between">
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.24em]" style={{ color: accent, fontFamily: mono }}>BUSINESS CASE</div>
              <SafeText className="mt-5 max-w-[70%] text-[40px] font-extrabold leading-[0.98] tracking-[-0.04em]" style={{ color: ink, fontFamily: sans }}>
                {copy.proof || 'La oferta reduce complejidad y acelera decisiones.'}
              </SafeText>
            </div>
            <div className="text-right" style={{ fontFamily: mono, color: muted }}>
              <div>ROI</div>
              <div>DELIVERY</div>
              <div>CONTROL</div>
            </div>
          </div>
          <div className="absolute bottom-10 left-10 right-10 grid grid-cols-3 gap-3">
            {['Claridad', 'Velocidad', 'Medición'].map((item, idx) => (
              <div key={item} className="border-t pt-5" style={{ borderColor: idx === 0 ? accent : 'rgba(15,23,42,0.14)' }}>
                <div className="text-[48px] font-extrabold leading-none" style={{ color: idx === 0 ? accent : navy, fontFamily: sans }}>
                  {idx === 0 ? '01' : idx === 1 ? '02' : '03'}
                </div>
                <div className="mt-3 text-[13px] font-bold uppercase tracking-[0.12em]" style={{ color: ink, fontFamily: sans }}>{item}</div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {index === 3 ? (
        <>
          <div className="absolute inset-0" style={{ background: navy }} />
          <div className="absolute right-0 top-0 h-full w-[46%]">
            <FullBleedImage {...getOfferVisual(draft, index, bg[3], { overlay: 'black', opacity: 0.42 })} />
          </div>
          <div className="absolute left-8 top-9 max-w-[52%] text-white">
            <div className="text-[11px] font-black uppercase tracking-[0.22em]" style={{ color: accent, fontFamily: mono }}>TIMELINE</div>
            <SafeText className="mt-6 text-[44px] font-extrabold leading-[0.96] tracking-[-0.045em]" style={{ fontFamily: sans }}>
              {copy.urgency || 'Este mes abrimos una nueva ventana de implementación.'}
            </SafeText>
          </div>
          <div className="absolute bottom-10 left-8 flex gap-3 text-white">
            {['Week 1', 'Week 2', 'Week 3'].map((item) => (
              <div key={item} className="rounded-full border border-white/20 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em]" style={{ fontFamily: mono }}>
                {item}
              </div>
            ))}
          </div>
        </>
      ) : null}

      {index === 4 ? (
        <>
          <div className="absolute inset-0" style={{ background: surface }} />
          <div className="absolute left-8 top-8 right-8 rounded-[34px] p-8" style={{ background: navy, color: '#fff' }}>
            <div className="text-[11px] font-black uppercase tracking-[0.24em]" style={{ color: accent, fontFamily: mono }}>NEXT STEP</div>
            <SafeText className="mt-6 max-w-[82%] text-[48px] font-extrabold leading-[0.94] tracking-[-0.05em]" style={{ fontFamily: sans }}>
              {copy.cta || 'Solicita una propuesta ejecutiva'}
            </SafeText>
          </div>
          <div className="absolute bottom-10 left-10 max-w-[54%] text-[15px] leading-[1.45]" style={{ color: muted, fontFamily: sans }}>
            {copy.subheadline}
          </div>
          <div className="absolute bottom-10 right-10 rounded-full px-6 py-3 text-[11px] font-black uppercase tracking-[0.16em] text-white" style={{ background: accent, fontFamily: sans }}>
            Contactar
          </div>
        </>
      ) : null}
    </OfferFrame>
  )
}
